import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { takeUntil } from 'rxjs';
import { DialogAddCommentData } from 'src/app/components/dialogs/add-comment/models/dialog-add-comment-data';
import { FolderPropertyCodes } from 'src/app/core/constantes/folder-property-codes';
import { BaseDirective } from 'src/app/core/directives/base.directive';

import { AttributeDefinitionsService } from '@hub4b/services/http/attribute-definitions.service';
import { FolderActionType } from 'src/app/models/enums/folder-action-type';
import { NavigateType } from 'src/app/models/enums/navigate-type';
import { MenuType } from 'src/app/models/events/folder-menu-refresh-event';
import { AttributeDefinition } from 'src/app/models/server/attribute-definition';
import { ViewType } from 'src/app/models/server/enums/view-type';
import { Folder } from 'src/app/models/server/folder';
import { FolderDocument } from 'src/app/models/server/folder-document';
import { Reminder } from 'src/app/models/server/reminder';
import { StandByDatas } from 'src/app/models/server/stand-by-datas';
import { View } from 'src/app/models/server/view';
import { DialogService } from 'src/app/services/actions/dialog.service';
import { NotificationService } from 'src/app/services/actions/notification.service';
import { EventsManagerService } from 'src/app/services/events/events-manager.service';
import { FolderMenuRefreshEventsService } from 'src/app/services/events/folder-menu-refresh-events.service';
import { DraftEmailsService } from 'src/app/services/http/draft-emails.service';
import { RemindersService } from 'src/app/services/http/reminders.service';
import { AttributeDefinitionsStateService } from 'src/app/services/state/attribute-definitions-state.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { DocumentsStateService } from 'src/app/services/state/documents-state.service';
import { FavoriteDocumentsStateService } from 'src/app/services/state/favorite-documents-state.service';
import { NavigationStateService } from 'src/app/services/state/navigate-state.service';
import { RemindersStateService } from 'src/app/services/state/reminders-state.service';
import { DetailBaseDatasource } from './datasources/detail-base-datasource';
import { FavoritesBarComponent } from './favorites-bar/favorites-bar.component';
import { FolderDetailMenuComponent } from './folder-detail-menu/folder-detail-menu.component';
import { SectionsContainerComponent } from './sections-container/sections-container.component';

@Component({
    templateUrl: './base-folder-detail.component.html',
    styleUrls: ['./base-folder-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FavoritesBarComponent, FolderDetailMenuComponent, NgClass, SectionsContainerComponent],
    providers: [
        {
            provide: FavoriteDocumentsStateService,
            useClass: FavoriteDocumentsStateService,
        },
    ],
})
export class BaseFolderDetailComponent extends BaseDirective implements OnInit {
    private attributeService = inject(AttributeDefinitionsService);
    private attributeDefinitionsStateService = inject(AttributeDefinitionsStateService);

    /**
     * Dossier dont le détail est affiché.
     */
    private _folder: Folder;

    @Input()
    public set folder(value: Folder) {
        this._folder = value;
        this.hasWarning = this._folder.hasWarning;

        if (this.view) {
            // Surcharge la vue en cas de vue archivée.
            this.folder.viewId = this._view.id;
        }
    }

    public get folder(): Folder {
        return this._folder;
    }

    /**
     * Vue à laquelle appartient le dossier.
     */
    @Input()
    public _view: View;

    @Input()
    public set view(value: View) {
        this._view = value;

        if (this.folder) {
            // Surcharge la vue en cas de vue archivée.
            this.folder.viewId = this._view.id;
        }
    }

    public get view(): View {
        return this._view;
    }

    /**
     * Valeur indiquant si le composant est affiché en mode consultation.
     */
    @Input()
    public readonly: boolean;

    /**
     * Liste des définitions des attributs utilisées par la section 'Attributs'.
     */
    public attributesDefinitions: AttributeDefinition[];

    /**
     * Liste des définitions des attributs utilisées par la section 'Favoris'.
     */
    public favoriteAttributesDefinitions: AttributeDefinition[];

    /** Valeur indiquant si la vue affichée est une vue 'Archive' */
    public isArchiveMode: boolean = false;

    /** Indique si le dossier possède des éléments necessitant l'attention de l'utilisateur */
    public hasWarning: boolean = false;

    /**
     * Constructeur.
     * @param remindersService
     * @param dialogService
     * @param connectedUserStateService
     * @param folderActionsService
     * @param notificationService
     * @param remindersStateService
     * @param navigateService
     * @param documentsStateService
     * @param definitionService
     * @param eventsManagerService
     * @param folderMenuRefreshEventsService
     * @param favoriteStateService
     * @param draftEmailsService

     * @param datasource
     */
    constructor(
        protected remindersService: RemindersService,
        protected dialogService: DialogService,
        protected connectedUserStateService: ConnectedUsersStateService,
        protected notificationService: NotificationService,
        protected remindersStateService: RemindersStateService,
        protected navigateService: NavigationStateService,
        protected documentsStateService: DocumentsStateService,
        protected definitionService: AttributeDefinitionsStateService,
        protected eventsManagerService: EventsManagerService,
        protected folderMenuRefreshEventsService: FolderMenuRefreshEventsService,
        protected favoriteStateService: FavoriteDocumentsStateService,
        protected draftEmailsService: DraftEmailsService,
        protected datasource: DetailBaseDatasource,
        protected cdr: ChangeDetectorRef
    ) {
        super();
    }

    /**
     * Appelée lorsque le composant est initialisé.
     */
    ngOnInit(): void {
        this.subscribeToEvents();

        // Récupération des définitions d'attributs de la vue.
        this.RetrieveAttributeDefinitions();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur un bouton d'action.
     * @param actionType : Type d'action.
     */
    public onActionRequest(actionType: FolderActionType): void {
        switch (actionType) {
            case FolderActionType.AddComment:
                this.addComment();
                break;
            case FolderActionType.AddFile:
                this.addFile();
                break;
            case FolderActionType.SendEmail:
                this.sendEmail();
                break;
            case FolderActionType.SendSms:
                this.sendSms();
                break;
            case FolderActionType.Delete:
                this.deleteFolder();
                break;
            case FolderActionType.SetInStandByState:
                this.setInStandByState();
                break;
            case FolderActionType.RemoveFromStandByState:
                this.removeFromStandByState();
                break;
            case FolderActionType.SetInWaitingArchivingState:
                this.setInWaitingArchivingState();
                break;
            case FolderActionType.RemoveFromWaitingArchivingState:
                this.removeFromWaitingArchivingState();
                break;
            case FolderActionType.AddReminder:
                this.addReminder();
                break;
            case FolderActionType.ArchiveFolder:
                this.archive();
                break;
            case FolderActionType.DisplayDraftEmails:
                this.displayDraftEmails();
                break;
            case FolderActionType.FollowFolder:
                this.followFolder();
                break;
            case FolderActionType.StopFollowFolder:
                this.stopFollowFolder();
                break;
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur un bouton de navigation.
     * @param navigationType : Type de navigation.
     */
    public onNavigationRequested(navigationType: NavigateType): void {
        this.navigateService.askNavigate({
            originalFolder: this.folder,
            currentFolder: this.folder,
            newFolder: null,
            navigateType: navigationType,
            view: this.view,
            readonly: this.readonly,
        });
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le menu 'Supprimer' d'un document.
     * @document Instance du document à supprimer.
     */
    public onDocumentDeletion(document: FolderDocument): void {
        this.datasource.deleteDocument(document.viewId, document.folderId, document.id).subscribe((x) => {
            this.notificationService.displaySuccess(`Le document \'${document.label}\' a bien été supprimé.`, '');

            this.refreshDocuments(null, true);

            // Les pièces-jointes du document qui étaient présente dans les favaoris sont retirées des favoris.
            document.attachments.forEach((attachment) => {
                if (attachment.isFavorite) {
                    this.favoriteStateService.removeFavoriteAttachmentFromVirtualDocument(attachment);
                }
            });
        });
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le menu 'Répondre au mail' d'un document.
     * @document Instance du document auquel répondre.
     */
    public onMailReplied(document: FolderDocument): void {
        this.replyToEmail(document);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le menu 'Renvoyer le mail' d'un document.
     * @document Instance du document auquel répondre.
     */
    public onMailResended(document: FolderDocument): void {
        this.resendEmail(document);
    }

    /**
     * * S'abonne aux événements des autres composants.
     */
    private subscribeToEvents(): void {
        // Souscrit aux evenements necessitant de raffraichir le menu du dossier.
        this.folderMenuRefreshEventsService.needRefresh.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            // Vérifie que l'evenement s'applique à ce dossier.
            if (event.folderId === this.folder.id) {
                //console.warn(`Réception de l'événement FolderMenuRefreshEvent par le composant DetailsComponent du dossier ${this.folder.id}`, event);
                let updatedFolder = Folder.from(this.folder);
                if (event.menuType === MenuType.StandBy) {
                    updatedFolder.isInStandByState = event.toggleValue;
                } else if (event.menuType === MenuType.FollowFolder) {
                    updatedFolder.isFollowed = event.toggleValue;
                } else {
                    updatedFolder.isInWaitingArchivingState = event.toggleValue;
                }

                this.folder = updatedFolder;
                this.cdr.markForCheck();
            }
        });
    }

    /**
     * Supprime le dossier.
     */
    private deleteFolder(): void {
        if (this.connectedUserStateService.getUserConnected().params.deletionConfirmationDisabled === true) {
            // Si l'utilisateur possède le paramètre 'Desactivation de la confirmation de suppression' à Vrai --> le dossier est supprimé directement.
            this.datasource.deleteFolder(this.folder);
        } else {
            // Si l'utilisateur possède le paramètre 'Desactivation de la confirmation de suppression' à Faux --> Affichage d'une demande de confirmation.
            this.dialogService.openDeleteFolder([this.folder]).subscribe((isConfirm) => {
                if (isConfirm) {
                    // Si l'utilisateur confirme la suppression --> le dossier est supprimé.
                    this.datasource.deleteFolder(this.folder);
                }
            });
        }
    }

    /**
     * Ouvre la boite de dialogue d'envoi de mail.
     */
    private sendEmail(): void {
        // Affiche une boite de dialogue pour permettre à l'utilisateur de remplir les informations du mail.
        this.dialogService.openSendEmail(this.view.id, this.folder.id).subscribe((document) => {
            if (document) {
                this.refreshDocuments(document.id);
            }
        });
    }

    /**
     * Ouvre la boite de dialogue pour répondre à un mail.
     * @param document : Document auquel répondre.
     */
    private replyToEmail(document: FolderDocument): void {
        // Affiche une boite de dialogue pour permettre à l'utilisateur de remplir les informations du mail.
        this.dialogService.openReplyToEmail(this.view.id, this.folder.id, document).subscribe((document) => {
            if (document) {
                this.refreshDocuments(document.id);
            }
        });
    }

    /**
     * Ouvre la boite de dialogue pour renvoyer un mail.
     * @param document : Document auquel répondre.
     */
    private resendEmail(document: FolderDocument): void {
        // Affiche une boite de dialogue pour permettre à l'utilisateur de remplir les informations du mail.
        this.dialogService.openResendEmail(this.view.id, this.folder.id, document).subscribe((document) => {
            if (document) {
                this.refreshDocuments(document.id);
            }
        });
    }

    /**
     * Ouvre la boite de dialogue d'envoi de SMS.
     */
    private sendSms(): void {
        // Affiche une boite de dialogue pour permettre à l'utilisateur de remplir les informations du SMS.
        this.dialogService.openSendSms(this.view.id, this.folder.id).subscribe((document) => {
            if (document) {
                this.refreshDocuments(document.id);
            }
        });
    }

    /**
     * Ajoute un ou (des) fichier(s) aux documents du dossier.
     */
    private addFile(): void {
        // Affiche une boite de dialogue pour permettre à l'utilisateur de selectionner les fichiers à ajouter.
        this.dialogService.openAddFile(this.view.id, this.folder.id).subscribe((document) => {
            if (document) {
                this.refreshDocuments(document.id);
                this.notificationService.displaySuccess('Les fichiers ont été ajouté au dossier.', '');
            }
        });
    }

    /**
     * Ajoute un commentaire au dossier.
     */
    private addComment(): void {
        // Affiche une boite de dialogue pour permettre à l'utilisateur de saisir les informations du commentaire.
        this.dialogService.openAddComment().subscribe((data: DialogAddCommentData) => {
            if (!!data) {
                this.datasource.addComment(this.view.id, this.folder.id, data).subscribe((document) => {
                    this.refreshDocuments(document.id);
                    this.notificationService.displaySuccess('Le commentaire a été ajouté au dossier.', '');
                });
            }
        });
    }

    /**
     * Ajoute un rappel au dossier.
     */
    private addReminder(): void {
        // Affiche une boite de dialogue pour permettre à l'utilisateur de saisir les informations du rappel.
        this.dialogService.openAddReminder(this.folder).subscribe((data: Reminder) => {
            if (!!data) {
                this.datasource.addReminder(this.view.id, this.folder.id, data).subscribe((x) => {
                    let reminders = this.remindersStateService.getRemindersList();

                    // Ajoute le rappel à la liste.
                    reminders.splice(0, 0, x);
                    this.remindersStateService.setRemindersList(reminders);

                    this.notificationService.displaySuccess('Le rappel a bien été créé.', '');
                });
            }
        });
    }

    /**
     * Place le dossier en veille.
     */
    private setInStandByState(): void {
        // Affiche une boite de dialogue pour permettre à l'utilisateur de saisir la durée de mise en veille ou la date de sortie automatique de la veille.
        this.dialogService
            .openSetInStandByState(this.connectedUserStateService.getUserConnected().params.standByDuration)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((result) => {
                if (!!result) {
                    const data = new StandByDatas();
                    data.duration = result.duration;
                    data.endDate = result.endDate;
                    this.datasource.setFolderInStandByState(this.folder, data);
                }
            });
    }

    /**
     * Sort le dossier de la veille
     */
    private removeFromStandByState(): void {
        this.datasource.removeFolderFromStandByState(this.folder);
    }

    /**
     * Place le dossier en 'attente d'archivage'.
     */
    private setInWaitingArchivingState(): void {
        this.datasource.setFolderInWaitingArchivingState(this.folder);
    }

    /**
     * Retire le dossier de l'état en 'attente d'archivage'.
     */
    private removeFromWaitingArchivingState(): void {
        this.datasource.removeFolderFromWaitingArchivingState(this.folder);
    }

    /**
     * Archive le dossier.
     */
    private archive(): void {
        this.datasource.archive(this.folder);
    }

    /**
     * Affiche la liste des brouillons de mail.
     */
    private displayDraftEmails() {
        this.documentsStateService.changeSelectedSubSection('DraftEmails');
    }

    /**
     * Suit le dossier.
     */
    private followFolder() {
        this.datasource.followFolder(this.folder);
    }

    /**
     * Arrête le suivi du dossier.
     */
    private stopFollowFolder() {
        this.datasource.stopFollowFolder(this.folder);
    }

    /**
     * Raffraichie la liste des documents.
     * @param selectedDocumentId : Id du document selectionné ou null si pas de document selectionné
     * @param isDeleted : Valeur indiquant que le raffraichissement est demandé suite à la suppression d'un document.
     */
    private refreshDocuments(selectedDocumentId: string = null, isDeleted: boolean = false): void {
        // Envoi une demande de raffraichissement de la section Historique pour afficher les entrées relatives à l'ajout/suppression de document.
        this.eventsManagerService.emitHistorySectionUpdatedEvent(this.view.id, this.folder.id);

        // Récupération de la liste des documents du dossier.
        this.datasource.listDocuments(this.view.id, this.folder.id).subscribe((documents) => {
            // Envoi un evenement indiquant que la liste des documents du dossier a été modifiée.
            this.eventsManagerService.emitDocumentsUpdatedEvent(this.folder.id, documents, selectedDocumentId, isDeleted);
        });

        // Envoi une demande de raffraichissement de la section 'Attributs' --> Pour mettre à jour l'attribut 'Date de mise à jour'.
        this.eventsManagerService.emitAttributesSectionUpdatedEvent(this.view.id, this.folder.id);

        // Envoi un evenement indiquant qu'un dossier a été modifé afin de mettre à jour la liste des dossiers et "Mes doosiers" --> Pour mettre à jour l'attribut 'Date de mise à jour'.
        this.eventsManagerService.emitListFolderUpdatedEvent(this.view.id, this.folder.id);
        this.eventsManagerService.emitFollowedFoldersUpdatedEvent(this.view.id, this.folder.id);
    }

    /**
     * Récupère les definitions des attributs.
     */
    private RetrieveAttributeDefinitions(): void {
        const attributeDefinitions = this.GetAttributeDefinitions();

        if (attributeDefinitions) {
            // Définie les attributs utilisés par la section 'attributs' en fonction du type de vue.
            this.attributesDefinitions = attributeDefinitions.filter(this.getFilterForAttributeSection());

            // Définie les attributs utilisés par la section 'favoris' en fonction du type de vue.
            this.favoriteAttributesDefinitions = attributeDefinitions.filter(this.getFilterForFavoriteSection());
        } else {
            // Si les definitions d'attributs de la vue ne sont pas encore chargées, on les récupère.
            // Cas si l'utilisateur ouvre un dossier depuis la liste "Mes dossiers" sans être passé par la "LISTE"
            this.attributeService.List(this.view.id).subscribe((attributes) => {
                this.attributeDefinitionsStateService.setAttributeDefinitions(this.view.id, attributes);

                // Définie les attributs utilisés par la section 'attributs' en fonction du type de vue.
                this.attributesDefinitions = attributes.filter(this.getFilterForAttributeSection());

                // Définie les attributs utilisés par la section 'favoris' en fonction du type de vue.
                this.favoriteAttributesDefinitions = attributes.filter(this.getFilterForFavoriteSection());
            });
        }
    }

    /**
     * Récupère les definitions d'attributs depuis le serveur et renseigne le moteur de rendu de chaque attribut.
     * @returns Les definitions d'attributs.
     */
    private GetAttributeDefinitions(): AttributeDefinition[] {
        return this.definitionService.getAttributeDefinitions(this.view.id);
    }

    /**
     * Retourne le filtre pour filtrer les attributs de la section 'Favoris'.
     * @returns Un filtre.
     */
    private getFilterForFavoriteSection(): (x) => boolean {
        return (x) =>
            x.isFavorite &&
            (x.propertyCode != FolderPropertyCodes.VIEW_ID || this.view.viewType == ViewType.Workflow || this.view.viewType == ViewType.ArchivingWorkflow) &&
            (x.propertyCode != FolderPropertyCodes.BOX_ID || this.view.viewType == ViewType.Box || this.view.viewType == ViewType.ArchivingBox);
    }

    /**
     * Retourne le filtre pour filtrer les attributs de la section 'Attributs'.
     * @returns Un filtre.
     */
    private getFilterForAttributeSection(): (x) => boolean {
        return (x) =>
            (x.propertyCode != FolderPropertyCodes.VIEW_ID || this.view.viewType == ViewType.Workflow || this.view.viewType == ViewType.ArchivingWorkflow) &&
            (x.propertyCode != FolderPropertyCodes.BOX_ID || this.view.viewType == ViewType.Box || this.view.viewType == ViewType.ArchivingBox);
    }
}
